import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { ErrorInterpretationDto, ErrorOwner } from '../../api/api';
import './Interpretation.scss';

/**
 * Who has to act, in the words the screen uses. "Wij" rather than a company name: the
 * dashboard is read by the team that runs the connector, and that is who "wij" is.
 */
export const OWNER_LABEL: Record<ErrorOwner, string> = {
    [ErrorOwner.Customer]: 'Klant',
    [ErrorOwner.Support]: 'Wij',
    [ErrorOwner.Automatic]: 'Vanzelf',
    [ErrorOwner.None]: 'Klaar',
};

export const OWNER_HINT: Record<ErrorOwner, string> = {
    [ErrorOwner.Customer]: 'De klant moet iets doen, in Exact of in Chargebee.',
    [ErrorOwner.Support]:
        'Wij moeten iets doen: de koppeling, de instellingen of de code.',
    [ErrorOwner.Automatic]:
        'Gaat vanzelf goed bij een nieuwe poging, of na één keer opnieuw aanbieden.',
    [ErrorOwner.None]: 'Niemand hoeft iets te doen.',
};

const OWNER_ICON: Record<ErrorOwner, IconProp> = {
    [ErrorOwner.Customer]: 'user',
    [ErrorOwner.Support]: 'gear',
    [ErrorOwner.Automatic]: 'rotate',
    [ErrorOwner.None]: 'circle-check',
};

/** A fixed-width badge saying who has to act, so a column of them lines up. */
export const OwnerBadge = (props: { owner?: ErrorOwner }) => {
    const owner = props.owner ?? ErrorOwner.Support;

    return (
        <span
            className={`owner-badge owner-badge--${owner.toLowerCase()}`}
            title={OWNER_HINT[owner]}
        >
            <FontAwesomeIcon icon={OWNER_ICON[owner]} />
            <span>{OWNER_LABEL[owner]}</span>
        </span>
    );
};

export interface IInterpretationNoteProps {
    interpretation?: ErrorInterpretationDto | null;

    /**
     * Two short lines - who and what, then what to do - for a table cell or a list row.
     * Without it the explanation, the steps and the details are written out as well.
     */
    compact?: boolean;

    /** Leaves the badge out, where the row already shows who has to act. */
    hideOwner?: boolean;

    /**
     * Leaves badge and title out and shows only what is behind them, for a row that was opened
     * under a line that already carries both.
     */
    bodyOnly?: boolean;
}

/**
 * What an error means and who has to act, next to the error as the connector wrote it.
 *
 * The error itself is never replaced: the reading can be wrong, and the original text is
 * what a developer searches the code for.
 */
export const InterpretationNote = ({
    interpretation,
    compact,
    hideOwner,
    bodyOnly,
}: IInterpretationNoteProps) => {
    if (!interpretation) {
        return null;
    }

    const head = (
        <div className="interpretation__head">
            {!hideOwner && <OwnerBadge owner={interpretation.owner} />}
            <span className="interpretation__title">
                {interpretation.title}
            </span>
        </div>
    );

    if (compact) {
        return (
            <div
                className="interpretation interpretation--compact"
                title={interpretation.explanation}
            >
                {head}
                {interpretation.action && (
                    <span className="interpretation__action">
                        {interpretation.action}
                    </span>
                )}
            </div>
        );
    }

    return (
        <div className="interpretation">
            {!bodyOnly && head}

            {interpretation.explanation && (
                <p className="interpretation__explanation">
                    {interpretation.explanation}
                </p>
            )}

            {(interpretation.details?.length ?? 0) > 0 && (
                <ul className="interpretation__details">
                    {interpretation.details!.map((detail) => (
                        <li key={detail}>{detail}</li>
                    ))}
                </ul>
            )}

            {(interpretation.steps?.length ?? 0) > 0 && (
                <ol className="interpretation__steps">
                    {interpretation.steps!.map((step) => (
                        <li key={step}>{step}</li>
                    ))}
                </ol>
            )}

            {interpretation.requeueHelps === false &&
                interpretation.owner !== ErrorOwner.None && (
                    <p className="interpretation__warning">
                        <FontAwesomeIcon icon="triangle-exclamation" /> Opnieuw
                        aanbieden lost dit niet op.
                    </p>
                )}
        </div>
    );
};
